import type { InvitationInspectResponse, LoginRequest, LoginResponse } from '@avrahami/contracts';
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { isApiError } from './api';
import { Alert, Button, Card, Loading, PasswordField, TextField } from './components';
import { formatDateTime, tokenFromHash } from './format';
import { DevCode } from './security';

/**
 * The screens both admins share before a session exists: sign-in with its second step, and the
 * invitation page. They hold no words of their own (each app passes its dictionary, which the
 * end-to-end tests select by) and no data layer: the app passes the calls.
 */

/** Full-page frame for the screens outside the signed-in app (sign-in, invitation). */
export function Centered({ product, children }: { product: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <p className="mb-4 text-center text-sm font-medium text-slate-500">{product}</p>
        {children}
      </div>
    </main>
  );
}

/** One async action's state: busy while it runs, its error until the next run. */
export function useAsyncAction<Args extends unknown[], Result>(
  action: (...args: Args) => Promise<Result>,
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = async (...args: Args): Promise<{ ok: true; value: Result } | { ok: false }> => {
    setBusy(true);
    setError(null);
    try {
      return { ok: true, value: await action(...args) };
    } catch (caught) {
      if (mounted.current) setError(caught);
      return { ok: false };
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return { run, busy, error };
}

export interface SignInWords {
  title: string;
  email: string;
  password: string;
  submit: string;
  codeTitle: string;
  codeHint: string;
  code: string;
  verify: string;
  startOver: string;
  invalid: string;
  wrongCode: string;
}

/**
 * Password first; when the account has a second factor (`mfaPending`), the code step follows.
 * The app's `login`, `verify` and `startOver` update its own session cache.
 */
export function SignInScreen({
  product,
  words,
  devCodeWords,
  mfaPending,
  login,
  verify,
  startOver,
  errorText,
  aside,
  forgotLink,
}: {
  product: string;
  words: SignInWords;
  devCodeWords: { label: string; use: string };
  mfaPending: boolean;
  login: (body: LoginRequest) => Promise<LoginResponse>;
  verify: (code: string) => Promise<unknown>;
  startOver: () => Promise<unknown>;
  errorText: (error: unknown) => string;
  /** Shown under the sign-in form (the tenant admin lists the stores already open there). */
  aside?: ReactNode;
  /** The app's link to its "forgot password" page (its router's own link). */
  forgotLink?: ReactNode;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // Development only: the code the API put on the screen (MFA_DEV_CODES).
  const [devCode, setDevCode] = useState<string>();
  const signIn = useAsyncAction(login);
  const check = useAsyncAction(verify);
  const restart = useAsyncAction(startOver);

  if (mfaPending) {
    const onVerify = (event: SyntheticEvent) => {
      event.preventDefault();
      void check.run(code.trim()).finally(() => {
        setCode('');
      });
    };
    return (
      <Centered product={product}>
        <Card title={words.codeTitle}>
          <form onSubmit={onVerify} className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">{words.codeHint}</p>
            <DevCode
              code={devCode}
              onUse={setCode}
              label={devCodeWords.label}
              useLabel={devCodeWords.use}
            />
            <TextField
              label={words.code}
              name="code"
              autoComplete="one-time-code"
              inputMode="text"
              autoFocus
              required
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
              className="ltr"
            />
            {check.error === null ? null : (
              <Alert>
                {isApiError(check.error, 'UNAUTHENTICATED')
                  ? words.wrongCode
                  : errorText(check.error)}
              </Alert>
            )}
            <Button type="submit" busy={check.busy}>
              {words.verify}
            </Button>
            <Button
              tone="secondary"
              disabled={restart.busy}
              onClick={() => {
                void restart.run();
              }}
            >
              {words.startOver}
            </Button>
          </form>
        </Card>
      </Centered>
    );
  }

  const onLogin = (event: SyntheticEvent) => {
    event.preventDefault();
    void signIn.run({ email: email.trim().toLowerCase(), password }).then((result) => {
      if (!result.ok) return;
      setPassword('');
      if (result.value.status === 'MFA_REQUIRED') setDevCode(result.value.devCode);
    });
  };
  return (
    <Centered product={product}>
      <Card title={words.title}>
        <form onSubmit={onLogin} className="flex flex-col gap-4">
          <TextField
            label={words.email}
            name="email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            className="ltr"
          />
          <PasswordField
            label={words.password}
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          {signIn.error === null ? null : (
            <Alert>
              {isApiError(signIn.error, 'UNAUTHENTICATED')
                ? words.invalid
                : errorText(signIn.error)}
            </Alert>
          )}
          <Button type="submit" busy={signIn.busy}>
            {words.submit}
          </Button>
          {forgotLink}
        </form>
      </Card>
      {aside}
    </Centered>
  );
}

export interface ForgotPasswordWords {
  title: string;
  hint: string;
  email: string;
  submit: string;
  sent: string;
  notSetUp: string;
}

/**
 * Asks for a "forgot password" link (adr/0035). The answer never tells whether the address has
 * an account: every accepted request shows the same message.
 */
export function ForgotPasswordScreen({
  product,
  words,
  request,
  loginLink,
  errorText,
}: {
  product: string;
  words: ForgotPasswordWords;
  request: (email: string) => Promise<unknown>;
  /** The app's link back to its sign-in page (its router's own link). */
  loginLink: ReactNode;
  errorText: (error: unknown) => string;
}) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const ask = useAsyncAction(request);

  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    void ask.run(email.trim().toLowerCase()).then((result) => {
      if (result.ok) setSent(true);
    });
  };
  return (
    <Centered product={product}>
      <Card title={words.title}>
        {sent ? (
          <div className="flex flex-col gap-4">
            <Alert tone="success">{words.sent}</Alert>
            {loginLink}
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">{words.hint}</p>
            <TextField
              label={words.email}
              name="email"
              type="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              className="ltr"
            />
            {ask.error === null ? null : (
              <Alert>
                {/* The API's own 503 (no platform mail account), not a network failure. */}
                {isApiError(ask.error, 'SERVICE_UNAVAILABLE') && ask.error.status === 503
                  ? words.notSetUp
                  : errorText(ask.error)}
              </Alert>
            )}
            <Button type="submit" busy={ask.busy}>
              {words.submit}
            </Button>
            {loginLink}
          </form>
        )}
      </Card>
    </Centered>
  );
}

export interface InvitationWords {
  title: string;
  invalid: string;
  missing: string;
  forUser: string;
  expires: string;
  password: string;
  confirm: string;
  mismatch: string;
  submit: string;
  done: string;
}

/**
 * Read the token from the fragment once, then drop it from the address bar and history, so it
 * does not linger on screen or in the browser's history after use.
 */
function takeTokenFromUrl(): string | undefined {
  const token = tokenFromHash(window.location.hash);
  if (window.location.hash !== '') {
    window.history.replaceState(null, '', window.location.pathname);
  }
  return token;
}

type Inspection =
  | { kind: 'loading' }
  | { kind: 'ready'; invitation: InvitationInspectResponse }
  | { kind: 'failed'; error: unknown };

/**
 * The public page behind an invitation link (set the first password) or a "forgot password" link
 * (set a new one): the two links work alike, the app passes the words and the calls.
 */
export function InvitationScreen({
  product,
  words,
  inspect,
  accept,
  loginLink,
  errorText,
}: {
  product: string;
  words: InvitationWords;
  inspect: (token: string) => Promise<InvitationInspectResponse>;
  accept: (token: string, password: string) => Promise<unknown>;
  /** The app's link to its sign-in page (its router's own link). */
  loginLink: ReactNode;
  errorText: (error: unknown) => string;
}) {
  const [token] = useState(takeTokenFromUrl);
  const [inspection, setInspection] = useState<Inspection>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);
  const save = useAsyncAction(accept);

  useEffect(() => {
    if (token === undefined) return;
    let cancelled = false;
    inspect(token).then(
      (invitation) => {
        if (!cancelled) setInspection({ kind: 'ready', invitation });
      },
      (error: unknown) => {
        if (!cancelled) setInspection({ kind: 'failed', error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token, inspect]);

  const failure = (error: unknown) =>
    isApiError(error, 'NOT_FOUND') ? words.invalid : errorText(error);

  if (token === undefined) {
    return (
      <Centered product={product}>
        <Alert>{words.missing}</Alert>
      </Centered>
    );
  }
  if (inspection.kind === 'loading') {
    return (
      <Centered product={product}>
        <Loading />
      </Centered>
    );
  }
  if (inspection.kind === 'failed') {
    return (
      <Centered product={product}>
        <Alert>{failure(inspection.error)}</Alert>
      </Centered>
    );
  }
  if (done) {
    return (
      <Centered product={product}>
        <Card title={words.title}>
          <div className="flex flex-col gap-4">
            <Alert tone="success">{words.done}</Alert>
            {loginLink}
          </div>
        </Card>
      </Centered>
    );
  }

  const { invitation } = inspection;
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    void save.run(token, password).then((result) => {
      if (result.ok) setDone(true);
    });
  };

  return (
    <Centered product={product}>
      <Card title={words.title}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <p className="text-sm text-slate-700">
            {words.forUser} <strong>{invitation.displayName}</strong>{' '}
            <span className="ltr">({invitation.email})</span>
          </p>
          <p className="text-xs text-slate-500">
            {words.expires} {formatDateTime(invitation.expiresAt)}
          </p>
          {/* Lets password managers file the new password under the right account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={invitation.email}
            readOnly
            hidden
          />
          <TextField
            label={words.password}
            name="new-password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
          <TextField
            label={words.confirm}
            name="confirm-password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={confirm}
            error={mismatch ? words.mismatch : undefined}
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
          />
          {save.error === null ? null : <Alert>{failure(save.error)}</Alert>}
          <Button type="submit" busy={save.busy}>
            {words.submit}
          </Button>
        </form>
      </Card>
    </Centered>
  );
}
